import { api } from '../../fetchWrapper';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import { epochToDateTime } from '../../utils';
import {
  ActivityLogData,
  ActivityLogSearchParams,
  ActivityLogList,
} from './activityLog.types';

/**
 * Parse description string that may contain "Description / Old Value / New Value" with various separators.
 * Handles: "Differences:" table (Field / Old Value / New Value columns), label-based, pipe/slash separated.
 */
function parseDescriptionParts(description: string): { descriptionSummary: string; oldValue: string; newValue: string; fieldName: string } {
  let descriptionSummary = '';
  let oldValue = '';
  let newValue = '';
  let fieldName = '';

  if (!description || typeof description !== 'string') {
    return { descriptionSummary, oldValue, newValue, fieldName };
  }

  const raw = description.trim();

  // 1) "Differences:" table – columns separated by 2+ spaces: Field  Old Value  New Value
  // Format: header line, separator (-----), then data rows with fieldName, oldVal, newVal
  if (raw.includes('Differences:')) {
    const lines = raw.split('\n');
    const dataRows: { field: string; old: string; new: string }[] = [];
    let pastSeparator = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/^\s*-+\s*$/.test(line)) {
        pastSeparator = true;
        continue;
      }
      if (!pastSeparator) continue;

      // Split by 2+ spaces (column separator)
      const parts = line.split(/\s{2,}/).map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 3) {
        dataRows.push({
          field: parts[0] || '',
          old: parts[1] || '',
          new: parts[2] || '',
        });
      }
    }

    if (dataRows.length > 0) {
      fieldName = dataRows[0].field;
      descriptionSummary = dataRows.map((r) => r.field).join(', ');
      oldValue = dataRows
        .filter((r) => !/^-+$/.test(r.old?.trim() || ''))
        .map((r) => `${r.field}: ${r.old}`)
        .join('\n');
      newValue = dataRows
        .filter((r) => !/^-+$/.test(r.new?.trim() || ''))
        .map((r) => `${r.field}: ${r.new}`)
        .join('\n');
      return { descriptionSummary, oldValue, newValue, fieldName };
    }
  }

  // 2) Label-based: "Description: X / Old Value: Y / New Value: Z"
  const descMatch = raw.match(/(?:Description|Field)\s*[:\-]\s*([^/|\n]+?)(?:\s*[\/|]\s*|Old Value)/i);
  const oldMatch = raw.match(/Old\s+Value\s*[:\-]\s*([^/|\n]+?)(?:\s*[\/|]\s*|New Value)/i);
  const newMatch = raw.match(/New\s+Value\s*[:\-]\s*([^/|\n]+?)(?:\s*[\/|]\s*|\n|$)/i);
  if (descMatch) {
    fieldName = descMatch[1].trim();
    descriptionSummary = fieldName;
  }
  if (oldMatch) {
    const v = oldMatch[1].trim();
    if (!/^-+$/.test(v)) oldValue = v;
  }
  if (newMatch) {
    const v = newMatch[1].trim();
    if (!/^-+$/.test(v)) newValue = v;
  }
  if (descriptionSummary || oldValue || newValue) {
    return { descriptionSummary, oldValue, newValue, fieldName };
  }

  // 3) Pipe or slash separated: "Description | Old Value | New Value"
  const pipeParts = raw.split(/\s*[|]\s*/);
  if (pipeParts.length >= 3) {
    descriptionSummary = pipeParts[0].trim();
    fieldName = descriptionSummary;
    const o = pipeParts[1].trim();
    const n = pipeParts[2].trim();
    if (!/^-+$/.test(o)) oldValue = o;
    if (!/^-+$/.test(n)) newValue = n;
    return { descriptionSummary, oldValue, newValue, fieldName };
  }

  const slashParts = raw.split(/\s*\/\s*/);
  if (slashParts.length >= 3) {
    descriptionSummary = slashParts[0].replace(/^(Description|Field)\s*[:\-]\s*/i, '').trim();
    fieldName = descriptionSummary || slashParts[0].trim();
    const o = slashParts[1].replace(/^Old\s+Value\s*[:\-]\s*/i, '').trim();
    const n = slashParts[2].replace(/^New\s+Value\s*[:\-]\s*/i, '').trim();
    if (!/^-+$/.test(o)) oldValue = o;
    if (!/^-+$/.test(n)) newValue = n;
    return { descriptionSummary, oldValue, newValue, fieldName };
  }

  // 4) Fallback: use full description as summary
  if (raw.length > 0 && raw.length < 500) {
    descriptionSummary = raw;
  }
  return { descriptionSummary, oldValue, newValue, fieldName };
}

// Extract display name from "UserName_Id.IP" (e.g. "Akshay_49.37.251.162" → "Akshay")
function getDisplayName(usernameWithId: string): string {
  if (!usernameWithId || typeof usernameWithId !== 'string') return '';
  const beforeUnderscore = usernameWithId.split('_')[0];
  return beforeUnderscore?.trim() || usernameWithId;
}

// Transform API response to UI data
// Response format: [0: activityLogId, 1: clientCode, 2: userNameWithId, 3: description, 4: clientId, 5: lastUpdatedDate, 6: lastConfiguredDate]
const transformActivityLog = (logArray: any[]): ActivityLogData => {
  const [id, code, modifiedUsername, description, clientId, lastUpdatedDate, lastConfiguredDate] = logArray;
  const { descriptionSummary, oldValue, newValue, fieldName } = parseDescriptionParts(description || '');

  const sanitize = (s: string | undefined) => {
    const t = (s || '').trim().replace(/\s+/g, '');
    return !t || /^-+$/.test(t) ? undefined : s;
  };

  return {
    id: id || 0,
    code: code || '',
    modifiedUsername: getDisplayName(modifiedUsername || ''),
    modifiedUsernameFull: modifiedUsername || '',
    description: description || '',
    clientId: clientId || 0,
    lastUpdatedDate: lastUpdatedDate || 0,
    lastConfiguredDate: lastConfiguredDate || 0,
    formattedLastUpdated: lastUpdatedDate ? epochToDateTime(lastUpdatedDate) : undefined,
    formattedLastConfigured: lastConfiguredDate ? epochToDateTime(lastConfiguredDate) : undefined,
    activityTypeLabel: code || 'Unknown',
    oldValue: sanitize(oldValue),
    newValue: sanitize(newValue),
    fieldName: fieldName || undefined,
    descriptionSummary: descriptionSummary || undefined,
  };
};

// Fetch activity logs
export async function fetchActivityLogs({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  clientId,
  userName,
  activityType,
  code: clientCodeFilter,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  clientId?: number;
  userName?: string;
  activityType?: string;
  code?: string;
  searched?: boolean;
}): Promise<ActivityLogList> {
  const params: any = {
    firstResult,
    maxResult,
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (userName) params.userName = userName;
  if (activityType) params.activityType = activityType;
  if (clientCodeFilter?.trim()) params.code = clientCodeFilter.trim();

  const endpoint = getEndpoint(clientCode, 'activityLogRest/getActivityLog');
  const response: any[] = await api.get(endpoint, params);
  
  // Handle array of arrays response
  return response.map(transformActivityLog);
}

// Fetch activity logs count
export async function fetchActivityLogsCount({
  clientCode,
  clientId,
  userName,
  activityType,
  code: clientCodeFilter,
  searched = true,
}: {
  clientCode: string;
  clientId?: number;
  userName?: string;
  activityType?: string;
  code?: string;
  searched?: boolean;
}): Promise<number> {
  const params: any = { searched };

  if (clientId) params.clientId = clientId;
  if (userName) params.userName = userName;
  if (activityType) params.activityType = activityType;
  if (clientCodeFilter?.trim()) params.code = clientCodeFilter.trim();

  const endpoint = getEndpoint(clientCode, 'activityLogRest/count');
  const response: any = await api.get(endpoint, params);
  
  return typeof response === 'number' ? response : response.count || 0;
}
